"use client";

import { CitationTool } from "@/components/citation-tool";
import { useToolUi } from "@/components/tools/tool-ui";
import { getFeaturedStyles } from "@/data/citation-styles";

/** English strings. A translated content file overrides these through `ui`. */
export const BROCHURE_CITATION_GENERATOR_STRINGS = {
  howToCiteABrochure: "How to Cite a Brochure",
  n1EnterTheBrochureDetails: "1. Enter the brochure details.",
  theProducingOrganizationUsuallyActs:
    'The producing organization usually acts as the author — enter its full name (e.g. "American Heart Association"). Add the brochure\'s title, the publisher, and the year; check the back panel for a print date. If you found it online, add the URL.',
  n2PickAStyle: "2. Pick a style.",
  chooseApaMlaChicagoOr:
    "Choose APA, MLA, Chicago, or any of the {styleCount} styles. APA adds a",
  brochure: "[Brochure]",
  descriptorAfterTheTitleAnd:
    "descriptor after the title — and when the author organization is also the publisher, APA drops the publisher rather than repeat the name.",
  n3Generate: "3. Generate.",
  weBuildAFullPamphlet:
    " We build a full pamphlet reference. Edit any field, then copy or export it to BibTeX, RIS, CSV, or Word.",
} as const;

/**
 * Live citation generator embedded on the Brochure landing page. Brochures and pamphlets
 * carry no machine-readable metadata, so the tool opens straight on the manual-entry tab
 * preset to the "Pamphlet / brochure" source type (CSL `pamphlet`). Users type the
 * organization, title, publisher, and year and pick any of the 10,834 styles — no engine
 * change.
 */
function BrochureGenerator() {
  return (
    <CitationTool
      initialManualType="pamphlet"
      initialMode="manual"
      styles={getFeaturedStyles()}
    />
  );
}

export default function BrochureCitationGeneratorWidget() {
  const strings = useToolUi(BROCHURE_CITATION_GENERATOR_STRINGS);

  return (
    <>
      {/* Live generator (manual entry preset to the Pamphlet / brochure source type) */}
      <BrochureGenerator />
      {/* How to */}
      <section className="space-y-4">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.howToCiteABrochure}
        </h2>
        <ol className="space-y-3 text-muted-foreground text-sm leading-relaxed">
          <li>
            <strong className="text-foreground">
              {strings.n1EnterTheBrochureDetails}
            </strong>{" "}
            {strings.theProducingOrganizationUsuallyActs}
          </li>
          <li>
            <strong className="text-foreground">{strings.n2PickAStyle}</strong>{" "}
            {strings.chooseApaMlaChicagoOr}{" "}
            <span className="font-medium text-foreground">
              {strings.brochure}
            </span>{" "}
            {strings.descriptorAfterTheTitleAnd}
          </li>
          <li>
            <strong className="text-foreground">{strings.n3Generate}</strong>
            {strings.weBuildAFullPamphlet}
          </li>
        </ol>
      </section>
    </>
  );
}

/**
 * English only for now. Add locales here as this page type is translated — the
 * registry in src/lib/i18n/localized-routes.ts has to agree, or the proxy will
 * redirect the URL this generates away again.
 */
export function generateStaticParams() {
  return [{ locale: "en" }];
}

export const dynamicParams = false;
